import { Typography, Box, Divider, IconButton, Card, CardContent } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HSTable from "../global/Table";

const CARD_SX = {
    display: "flex",
    flexDirection: "column",
    borderRadius: 3,
    boxShadow: 3,
    overflow: "hidden",
    position: "relative",
    marginTop: "20px",
};

const CLOSE_BUTTON_SX = {
    position: "absolute",
    top: 8,
    right: 8,
    color: "grey.600",
    zIndex: 1,
};

const SECTION_BOX_SX = {
    borderRadius: 2,
    p: 2,
};

const ITEM_BOX_SX = {
    display: "flex",
    justifyContent: "space-between",
    mb: 1,
};

const formatValue = (val) => (val === undefined || val === null || val === "" ? "—" : val);

const formatPrice = (val) => {
    if (val == null || val === "") return "—";
    const number = Number(val);
    if (isNaN(number)) return "—";
    return number.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
};

const formatDimensions = (dims) => {
    if (!dims) return [];
    return [
        { label: "Diâmetro Ø", value: dims.diameter ? `${dims.diameter} mm` : "—" },
        { label: "Comprimento", value: dims.length ? `${dims.length} mm` : "—" },
        { label: "Espessura", value: dims.thickness ? `${dims.thickness} mm` : "—" },
        { label: "Largura", value: dims.width ? `${dims.width} mm` : "—" },
    ].filter(item => item.value !== "—");
};

const getBudgetSections = (budget) => [
    {
        title: "Torre",
        subsections: [
            {
                title: "Dimensões",
                items: formatDimensions(budget.tower?.dimensions),
            },
            {
                title: "Material & Preço",
                items: [
                    { label: "Material", value: formatValue(budget.tower?.material) },
                    { label: "Preço de Venda", value: formatPrice(budget.tower?.totalSalePrice) },
                ],
            },
        ],
    },
    {
        title: "Tanque",
        subsections: [
            {
                title: "Dimensões",
                items: formatDimensions(budget.tank?.dimensions),
            },
            {
                title: "Material & Preço",
                items: [
                    { label: "Material", value: formatValue(budget.tank?.material) },
                    { label: "Preço de Venda", value: formatPrice(budget.tank?.totalSalePrice) },
                ],
            },
        ],
    },
    {
        title: "Condições de Funcionamento",
        items: [
            { label: "Capacidade", value: budget.capacityTonPerHour ? `${budget.capacityTonPerHour} ton/h` : "—" },
            { label: "Temperatura", value: budget.operatingTemperatureCelsius ? `${budget.operatingTemperatureCelsius} ºC` : "—" },
            { label: "Pressão", value: budget.operatingPressureBar ? `${budget.operatingPressureBar} bar` : "—" },
        ],
    },
    {
        title: "Transporte & Total",
        items: [
            { label: "Transporte", value: budget.transport?.included ? "Sim" : "Não" },
            { label: "Preço Compra Transporte", value: formatPrice(budget.transport?.purchasePrice) },
            { label: "Preço Venda Transporte", value: formatPrice(budget.transport?.salePrice) },
            { label: "Total Venda", value: formatPrice(budget.totalSalePrice) },
        ],
    },
];

const itemColumns = [
    { field: "description", label: "Descrição", sortable: true },
    { field: "type", label: "Tipo", sortable: true },
    { field: "material", label: "Material", sortable: true, render: formatValue },
    { field: "dimensions", label: "Ø mm", render: (dims) => dims?.diameter ?? "—" },
    { field: "dimensions", label: "C mm", render: (dims) => dims?.length ?? "—" },
    { field: "dimensions", label: "E mm", render: (dims) => dims?.thickness ?? "—" },
    { field: "purchasePrice", label: "Preço Compra", sortable: true, render: formatPrice },
];

export default function DeaeratorBudgetDetail({ budget, onClose }) {
    if (!budget) return null;

    const sections = getBudgetSections(budget);

    // Flatten all items from tower, tank and miscellaneous into one table
    const allItems = [
        ...(budget.tower?.items ?? []).map(i => ({ ...i, _section: "Tower" })),
        ...(budget.tank?.items ?? []).map(i => ({ ...i, _section: "Tank" })),
        ...(budget.miscellaneous?.items ?? []).map(i => ({ ...i, _section: "Misc." })),
    ];

    const columnsWithSection = [
        { field: "_section", label: "Secção", sortable: true },
        ...itemColumns,
    ];

    return (
        <Card sx={CARD_SX}>
            {onClose && (
                <IconButton onClick={onClose} sx={CLOSE_BUTTON_SX}>
                    <CloseIcon />
                </IconButton>
            )}

            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
               
                <CardContent sx={{ flex: 2, p: 3 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {budget.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" paragraph>
                        {budget.sourceFile}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
                            gap: 3,
                        }}
                    >
                        {sections.map((section, idx) => (
                            <Box key={idx} sx={SECTION_BOX_SX}>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    gutterBottom
                                    align="center"
                                    sx={{ mb: "10px" }}
                                >
                                    {section.title}
                                </Typography>

                                {section.subsections
                                    ? section.subsections.map((sub, sIdx) => (
                                        <Box key={sIdx} sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                                {sub.title}
                                            </Typography>
                                            {sub.items.map((item, iIdx) => (
                                                <Box key={iIdx} sx={ITEM_BOX_SX}>
                                                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                                    <Typography variant="body2">{item.value}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    ))
                                    : section.items.map((item, iIdx) => (
                                        <Box key={iIdx} sx={{ ...ITEM_BOX_SX, mt: iIdx === 0 ? "40px" : "0px" }}>
                                            <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                            <Typography variant="body2">{item.value}</Typography>
                                        </Box>
                                    ))}
                            </Box>
                        ))}
                    </Box>

                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 2, mb: 2 }}>
                        Materiais
                    </Typography>

                    <HSTable
                        columns={columnsWithSection}
                        data={allItems}
                        loading={false}
                        page={0}
                        rowsPerPage={allItems.length}
                        enableSearch={false}
                        totalCount={allItems.length}
                        onRowsPerPageChange={() => {}}
                        onPageChange={() => {}}
                        onSearchChange={() => {}}
                    />
                </CardContent>
            </Box>
        </Card>
    );
}